import styled from 'styled-components';

import { PromotionRecommendationsGroupedByProduct } from 'common/api-types/recommendations';
import { ApiErrorNotification } from 'common/components/cookbook/ApiErrorNotification';
import { ContentContainer } from 'common/components/cookbook/ContentContainer';
import { DataTableSubRows } from 'common/components/cookbook/DataTable/DataTableSubRows';
import { Stack } from 'common/components/cookbook/Stack';
import { TextLink } from 'common/components/cookbook/TextLink';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';
import { useUserLocale } from 'common/hooks/useUserLocale';
import { useVenuesQuery } from 'common/hooks/useVenuesQuery';
import { formatMoney } from 'common/utils/moneyUtils';
import { formatNumber } from 'common/utils/numberUtils';
import { RecommendationReasons } from 'features/selection-recommendations/common/RecommendationReasons/RecommendationReasons';
import { getListingDuration } from 'features/selection-recommendations/utils';

type Props = {
  parent: PromotionRecommendationsGroupedByProduct & { id: string };
};

const Venue = styled(Stack)`
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const PromotionRecommendationSubList = ({ parent }: Props) => {
  const f = useFormatMessage();
  const countryCodes = useSelectedCountryCodes();
  const locale = useUserLocale();
  const { data: listMetaVenues, error } = useVenuesQuery({ countryCodes });

  if (error) {
    return (
      <ContentContainer>
        <ApiErrorNotification error={error} />
      </ContentContainer>
    );
  }

  return (
    <DataTableSubRows
      dataSource={parent.venueItems}
      columns={[
        {
          id: 'venues',
          header: '',
          width: 450,
          render: ({ record: { venueId } }) => {
            const venue = listMetaVenues?.items?.find((v) => v.id === venueId);
            return <Venue>{venue?.name}</Venue>;
          },
        },
        {
          id: 'expectedBusinessImpact',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          width: 150,
          header: f('selection_recommendations.expected_business_impact'),
          render: ({ record: { expectedBusinessImpact } }) => {
            return expectedBusinessImpact?.revenueImpact?.amount !== undefined
              ? formatMoney(locale, expectedBusinessImpact.revenueImpact)
              : '-';
          },
        },
        {
          id: 'reasons',
          alignNameVertical: 'middle',
          header: f('selection_recommendations.reason'),
          render: ({ record: { reasons } }) => {
            return <RecommendationReasons reasons={reasons} />;
          },
        },
        {
          id: 'lastPromotion',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          width: 120,
          header: f('selection_recommendations.last_promotion'),
          render: ({ record: { lastPromotionEndedAt } }) => {
            if (!lastPromotionEndedAt) return '-';
            const listingDuration = getListingDuration(lastPromotionEndedAt, locale);
            return f('selection_recommendations.last_promotion_ago', {
              count: listingDuration,
            });
          },
        },
        {
          id: 'promoShare',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          width: 120,
          header: f('selection_recommendations.promo_share'),
          render: ({ record: { promoShare } }) => {
            if (!promoShare) return '-';
            return `${formatNumber(locale, promoShare)}%`;
          },
        },
        {
          id: 'salesPrice',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 150,
          header: f('selection_recommendations.sales_price_including_vat'),
          render: ({ record: { salesPrice } }) => formatMoney(locale, salesPrice),
        },
        {
          id: 'soldUnits',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 150,
          header: f('selection_recommendations.sold_units'),
          render: ({ record: { soldUnitsPast90Days } }) => {
            if (!soldUnitsPast90Days) return '-';
            return formatNumber(locale, soldUnitsPast90Days);
          },
        },
        {
          id: 'costPrice',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 150,
          header: f('selection_recommendations.cost_price_excluding_vat'),
          render: ({ record: { costPrice } }) => {
            if (!costPrice) return '-';
            return formatMoney(locale, costPrice);
          },
        },
        {
          id: 'ppm',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 150,
          header: f('selection_recommendations.ppm_percentage'),
          render: ({ record: { pureProfitMargin } }) => {
            if (!pureProfitMargin) return '-';
            return `${formatNumber(locale, pureProfitMargin, 2)}%`;
          },
        },
        {
          id: 'ppmGap',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 150,
          header: (
            <>
              <div>{f('selection_recommendations.ppm_delta')}</div>
              <div>{f('selection_recommendations.ppm_delta_explanation')}</div>
            </>
          ),
          render: ({ record: { pureProfitMarginGapBps } }) => {
            const ppmGap = formatNumber(locale, pureProfitMarginGapBps);

            return pureProfitMarginGapBps && pureProfitMarginGapBps > 0
              ? `+${ppmGap}`
              : ppmGap || '-';
          },
        },
        {
          id: 'priceIndex',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          width: 120,
          header: f('selection_recommendations.price_index'),
          render: ({ record: { priceIndex } }) => {
            if (!priceIndex) return '-';
            return formatNumber(locale, Number(priceIndex), 2);
          },
        },
        {
          id: 'stockCount',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 120,
          header: f('selection_recommendations.stock_count'),
          render: ({ record: { stockCount } }) => {
            if (!stockCount) return '-';
            return formatNumber(locale, stockCount);
          },
        },
        {
          id: 'vendors',
          alignNameVertical: 'middle',
          width: 200,
          header: f('selection_recommendations.vendor'),
          render: ({ record: { vendor } }) => {
            if (!vendor) return '-';
            return (
              <TextLink to="/vendors/$vendorId" params={{ vendorId: vendor.id }} target="_blank">
                {vendor.name}
              </TextLink>
            );
          },
        },
        {
          id: 'basketPenetration',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          width: 160,
          header: f('selection_recommendations.basket_penetration'),
          render: ({ record: { basketPenetrationPercentage } }) => {
            return basketPenetrationPercentage
              ? `${formatNumber(locale, basketPenetrationPercentage, 2)}%`
              : '-';
          },
        },
        {
          id: 'basketPenetrationPHL2Median',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          width: 160,
          header: f('selection_recommendations.basket_penetration_phl2_median'),
          render: ({ record: { basketPenetrationPercentagePHL2Median } }) => {
            return basketPenetrationPercentagePHL2Median
              ? `${formatNumber(locale, basketPenetrationPercentagePHL2Median, 2)}%`
              : '-';
          },
        },
        {
          id: 'profitPerUnit',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          width: 140,
          header: f('selection_recommendations.profit_per_unit'),
          render: ({ record: { profitPerUnit } }) => {
            if (!profitPerUnit) return '-';
            return formatMoney(locale, profitPerUnit);
          },
        },
        {
          id: 'profitPerUnitPHL2Median',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          width: 140,
          header: f('selection_recommendations.profit_per_unit_phl2_median'),
          render: ({ record: { profitPerUnitPHL2Median } }) => {
            if (!profitPerUnitPHL2Median) return '-';
            return formatMoney(locale, profitPerUnitPHL2Median);
          },
        },

        {
          id: 'revenue',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          header: f('selection_recommendations.revenue'),
          render: ({ record: { totalRevenue } }) => {
            if (!totalRevenue) return '-';
            return formatMoney(locale, totalRevenue);
          },
        },
      ]}
      parentRow={parent}
    />
  );
};
